import React, { useEffect, useState } from "react";
import {
  Form,
  InputNumber,
  Button,
  message,
  Input,
  Popconfirm,
  Radio,
  Select,
  Space,
} from "antd";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import { printBill } from "../../../../components/Receptionist/receptionKeys";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import {
  addPaymentApi,
  shareBillApi,
} from "../../../../services/receptionist.services";
import PaymentModeSelector from "./PaymentModeSelector";
import { formatToINR } from "../../../../helpers/utility";
import styles from "./BillingIp.module.css";
import generateIcon from "../../../../assets/receptionistIcons/ipBilling/generate.svg";
import {
  createCashAmountRule,
  syncCashAmountValidation,
  validateCashPaymentAmount,
} from "../../../../helpers/paymentValidation";

/* Same two units the final settlement offers, so a discount reads the same
   way wherever it is entered. */
const priceTagList = [
  { name: "₹", id: false },
  { name: "%", id: true },
];

const PaymentModeField = ({ value, onChange, form }) => (
  <PaymentModeSelector
    value={value}
    onChange={(mode) => {
      onChange?.(mode);
      syncCashAmountValidation(form, mode, "amount");
    }}
  />
);

const AdvancePayment = ({ billData, refreshData, activePatient }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const paymentMode = Form.useWatch("mode", form);
  const discountValue = Form.useWatch("discount", form);
  const isPercentValue = Form.useWatch("is_percent", form);

  const amountValue = Form.useWatch("amount", form);

  const allowDiscount = activePatient === false;
  const paymentDetails = billData?.data?.payment_details;
  const dueAmountTotal = Number(paymentDetails?.total_due_amount || 0);
  const hasDueAmount = dueAmountTotal > 0;
  /* A discharged patient can settle in full, pay part of what is owed, or
     leave the balance outstanding - the same three choices the final
     settlement offers while the patient is still admitted. */
  const allowPartialPayment = activePatient === false && hasDueAmount;
  const [paymentChoice, setPaymentChoice] = useState(null);
  const partialPayment = paymentChoice === "partial";
  const continueWithDue = paymentChoice === "due";

  /* Re-picking the selected option clears it, so full settlement is reachable
     without a third radio. Switching into a partial payment hands the amount
     back to the receptionist to type. */
  const choosePayment = (next) => {
    const value = paymentChoice === next ? null : next;
    setPaymentChoice(value);
    /* A discount belongs to the settlement it was entered against, so moving
       between partial, due and full settlement clears it rather than carrying
       a figure over to a sum it was not meant for. The field stays on screen
       so a fresh discount can be given straight away. */
    setGrossAmount(null);
    form.setFieldsValue({ discount: undefined });
    form.setFields([{ name: "discount", errors: [] }]);
    if (value === "partial") form.setFieldsValue({ amount: undefined });
  };


  const resolveDiscountInRupees = (discount, isPercentMode) => {
    if (!discount) return 0;
    if (isPercentMode) {
      return Number(((Number(discount) * dueAmountTotal) / 100).toFixed(2));
    }
    return Number(discount);
  };

  const amountAfterDiscount = (discount, isPercentMode) => {
    const rupees = resolveDiscountInRupees(discount, isPercentMode);
    return Math.max(0, Number((dueAmountTotal - rupees).toFixed(2)));
  };

  /* What the receptionist typed before any discount came off it. Held in
     state, not a ref, so the due figure below re-renders as it is typed -
     the amount field itself only settles to the discounted figure on blur,
     so it is never rewritten mid-keystroke. */
  const [grossAmount, setGrossAmount] = useState(null);

  /* Nothing to collect means nothing to collect it by. Continuing with the
     due is one way to get here; a discount that covers the whole balance is
     another, and that one still asked for a payment mode it could not use. */
  const collectsNothing = continueWithDue || !Number(amountValue || 0);

  const dueAfterDiscount = amountAfterDiscount(discountValue, isPercentValue);
  const discountInRupeesNow = resolveDiscountInRupees(
    discountValue,
    isPercentValue
  );
  /* The bill is credited the cash collected plus the discount written off
     with it - that total is the figure originally typed. Reading it from
     `grossAmount` keeps this correct while the amount field still shows the
     typed figure, before blur settles it to the discounted one. */
  const settledTotal =
    grossAmount != null
      ? grossAmount
      : Number(amountValue || 0) + discountInRupeesNow;
  const remainingDue = Math.max(
    0,
    Number((dueAmountTotal - settledTotal).toFixed(2))
  );

  useEffect(() => {
    if (!partialPayment) return;
    if (grossAmount == null) return;
    form.setFieldsValue({
      amount: Math.max(
        0,
        Number((grossAmount - discountInRupeesNow).toFixed(2))
      ),
    });
    /* Deliberately NOT keyed on `grossAmount`: it changes on every keystroke
       in the amount field, and re-netting mid-type collapses a half-typed
       figure to zero the moment it is smaller than the discount - which makes
       the field impossible to type in. The discount is applied when the
       discount itself moves, and on blur of the amount. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountValue, isPercentValue, partialPayment]);

  useEffect(() => {
    syncCashAmountValidation(form, paymentMode, "amount");
  }, [paymentMode, form]);


  /* Outside a partial payment the amount is not the receptionist's to type -
     it is whatever is left of the due after the discount. Entering a discount
     therefore moves this field and nothing else on the form. */
  useEffect(() => {
    if (activePatient) return;
    if (dueAmountTotal <= 0) return;
    if (partialPayment) return;
    form.setFieldsValue({
      amount: continueWithDue
        ? 0
        : amountAfterDiscount(discountValue, isPercentValue),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activePatient,
    dueAmountTotal,
    partialPayment,
    continueWithDue,
    discountValue,
    isPercentValue,
    form,
  ]);

  const onFinish = (values) => {
    /* Continuing with the due collects nothing now, so there is no mode or
       transaction to record against it and no cash ceiling to check. */
    const payingAmount = continueWithDue ? 0 : Number(values?.amount ?? 0);
    const isZeroPayment = !payingAmount;

    if (!isZeroPayment) {
      const cashError = validateCashPaymentAmount(payingAmount, values?.mode);
      if (cashError) {
        message.error(cashError);
        return;
      }
    }

    setLoading(true);
    const { is_percent, discount, ...rest } = values;
    const payload = {
      ...rest,
      amount: payingAmount,
      mode: isZeroPayment ? undefined : values?.mode,
      transaction_number: isZeroPayment ? undefined : values?.transaction_number,
      remarks: isZeroPayment ? undefined : values?.remarks,
      ...(allowDiscount
        ? { discount: resolveDiscountInRupees(discount, is_percent) }
        : {}),
    };

    addPaymentApi(billData?.data?.bill?.id, payload)
      .then((res) => {
        message.success(res.data.message);
        form.resetFields();
        refreshData();
        printBill(res?.data?.data?.bill, res?.data?.data?.id, true);
        setLoading(false);
      })
      .catch((err) => {
        message.error(err.response?.data?.message);
        setLoading(false);
      });
  };

  const downLoadBill = (id) => {
    shareBillApi(id, `type=bill&share=download`)
      .then((res) => {
        const s3Link = res?.data?.data;
        if (s3Link) {
          window.open(s3Link, "_blank");
        } else {
          message.error("Failed to retrieve download link.");
        }
      })
      .catch(() => message.error("Error downloading the bill."));
  };

  const shareBill = (id) => {
    shareBillApi(id, `type=bill&share=email`)
      .then(() => message.success("Bill Shared Successfully"))
      .catch((err) => message.error(err?.response?.data?.message));
  };

  return (
    <Form
      layout="vertical"
      onFinish={onFinish}
      form={form}
      initialValues={{ is_percent: false }}
    >
      {allowPartialPayment && (
        <Form.Item className={styles.partialCheckbox}>
          <Radio.Group
            value={paymentChoice}
            onChange={(e) => choosePayment(e.target.value)}
          >
            <Space direction="vertical">
              <Radio
                value="partial"
                onClick={() => partialPayment && choosePayment("partial")}
              >
                Partial payment
              </Radio>
              <Radio
                value="due"
                onClick={() => continueWithDue && choosePayment("due")}
              >
                Continue with Due Amount
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
      )}

      {!continueWithDue && (
        <>
          <label className={styles.formLabel} htmlFor="payment-amount">
            Amount
          </label>
          <Form.Item
            name="amount"
            className={styles.amountInput}
            rules={[
              { required: true, message: "Please enter amount" },
              createCashAmountRule(form, "mode"),
            ]}
          >
            <InputNumber
              id="payment-amount"
              addonBefore="₹"
              controls={false}
              min={0}
              disabled={allowPartialPayment && !partialPayment}
              placeholder="0.00"
              onChange={(value) => {
                if (partialPayment) setGrossAmount(Number(value || 0));
              }}
              /* Settles the typed figure against the discount once the
                 receptionist leaves the field, so a discount entered before
                 the amount still comes off it. Recomputed from the typed
                 figure, so leaving the field twice is not a double discount. */
              onBlur={() => {
                if (!partialPayment) return;
                if (grossAmount == null) return;
                form.setFieldsValue({
                  amount: Math.max(
                    0,
                    Number((grossAmount - discountInRupeesNow).toFixed(2))
                  ),
                });
              }}
              className={`${inputStyles.mdNumberInput} ${styles.settlementAmountInput}`}
            />
          </Form.Item>
        </>
      )}

      {allowDiscount && (
        <>
          <label className={styles.formLabel} htmlFor="payment-discount">
            Discount
          </label>
          <Form.Item className={styles.discountField}>
            <Space.Compact block className={styles.discountCompact}>
              <Form.Item
                name="discount"
                noStyle
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const isPercentMode = form.getFieldValue("is_percent");
                      if (isPercentMode) {
                        if (Number(value) > 100) {
                          return Promise.reject(
                            "Discount cannot be greater than 100%"
                          );
                        }
                        if (
                          resolveDiscountInRupees(value, true) > dueAmountTotal
                        ) {
                          return Promise.reject(
                            "Discount cannot be greater than due amount"
                          );
                        }
                      } else if (Number(value) > dueAmountTotal) {
                        return Promise.reject(
                          "Discount cannot be greater than due amount"
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  id="payment-discount"
                  min={0}
                  controls={false}
                  placeholder="0"
                  className={inputStyles.mdNumberInput}
                  onChange={() => {
                    form.setFields([{ name: "amount", errors: [] }]);
                    form.validateFields(["amount"]);
                  }}
                />
              </Form.Item>
              <Form.Item name="is_percent" noStyle>
                <Select
                  className={inputStyles.mdSelect}
                  onChange={() => {
                    form.setFields([{ name: "discount", errors: [] }]);
                    form.validateFields(["discount"]);
                  }}
                >
                  {priceTagList.map((item) => (
                    <Select.Option key={String(item.id)} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Space.Compact>
          </Form.Item>
        </>
      )}

      {partialPayment && (
        <p className={styles.dueHint}>
          Due amount: {formatToINR(remainingDue.toFixed(2))}
        </p>
      )}

      {continueWithDue && (
        <p className={styles.dueHint}>
          No payment will be collected now.{" "}
          {formatToINR(dueAfterDiscount.toFixed(2))} stays outstanding.
        </p>
      )}

      {!collectsNothing && (
        <>
          <label className={styles.formLabel} style={{ marginTop: 16 }}>
            Payment mode
          </label>
          <Form.Item
            name="mode"
            rules={[{ required: true, message: "Please select a payment mode" }]}
          >
            <PaymentModeField form={form} />
          </Form.Item>

          {paymentMode && paymentMode !== "cash" && (
            <>
              <label className={styles.formLabel} style={{ marginTop: 16 }}>
                Transaction number
              </label>
              <Form.Item name="transaction_number">
                <Input
                  className={inputStyles.mdInput}
                  placeholder="Enter transaction ID"
                />
              </Form.Item>
            </>
          )}
        </>
      )}

      <label className={styles.formLabel} style={{ marginTop: 16 }}>
        Remarks
      </label>
      <Form.Item name="remarks" className={styles.remarksInput}>
        <Input.TextArea placeholder="Optional note for receipt" rows={3} />
      </Form.Item>

      <div className={styles.settlementActions}>
        <Popconfirm
          title="Payment confirmation"
          description="Have you verified the payment details?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => form.submit()}
        >
          <Button
            loading={loading}
            type="primary"
            className={`${btnStyles.lgBtn} ${styles.submitBtn}`}
            icon={
              <img src={generateIcon} alt="" className={styles.submitBtnIcon} />
            }
          >
            Generate receipt
          </Button>
        </Popconfirm>
      </div>

      {!activePatient && (
        <div className={styles.dischargedBillActions}>
          <div className={styles.dischargedBillRow}>
            <Button
              loading={loading}
              type="primary"
              className={`${btnStyles.mdBtn} ${styles.dischargedBillBtn}`}
              onClick={() => downLoadBill(billData?.data?.bill?.id)}
            >
              Download Bill
            </Button>
            <Button
              loading={loading}
              type="primary"
              ghost
              className={`${btnStyles.mdBtn} ${styles.dischargedBillBtn}`}
              onClick={() => shareBill(billData?.data?.bill?.id)}
            >
              Share Bill
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default AdvancePayment;
